// Whether a wellness-recall row can open a patient's chart.
//
// The recall list is keyed by the imported patients database; charts are keyed
// by the contact directory. The phone is the only bridge, and phones are
// shared — a household line carries a couple, and the directory also holds
// duplicate records of the same person. Picking the first match would show a
// doctor somebody else's chart, so this refuses rather than guesses.

export interface ContactCandidate {
  internalId: string;
  fullName: string;
}

export interface WellnessContactLink {
  /** The contact to open, or null when we must not choose one. */
  internalId: string | null;
  reason?: 'no-match' | 'ambiguous' | 'household';
}

const samePerson = (name: string) => String(name || '').toLowerCase().replace(/[^a-z]/g, '');

// A contact standing for more than one person — "Mark and Fran Levy". The
// joining word has to stand alone: plenty of real names carry those letters
// inside them (Amanda, Sandoval, Anderson).
const isJointRecord = (name: string) => /\s(?:and|&|\+)\s/i.test(String(name || ''));

/**
 * The contact this row may open, or why it may not open one.
 *
 * Several records naming the same person are that person — duplicates in the
 * directory, either is right. Several records naming different people are a
 * shared phone, and choosing between them is not ours to do.
 */
export function resolveWellnessContact(
  candidates: ContactCandidate[] | undefined | null,
): WellnessContactLink {
  const usable = (candidates || []).filter((c) => c && c.internalId);
  if (usable.length === 0) return { internalId: null, reason: 'no-match' };

  // Checked before counting names: a household record is one row naming two
  // people, so it looks unambiguous by count and is not.
  if (usable.some((c) => isJointRecord(c.fullName))) {
    return { internalId: null, reason: 'household' };
  }

  const people = new Set(usable.map((c) => samePerson(c.fullName)));
  if (people.size > 1) return { internalId: null, reason: 'ambiguous' };

  return { internalId: usable[0].internalId };
}
